import type { CellBox } from "./frame-depth.ts";
import { FOOTPRINT_DEFAULT_CELL, footprintCellIssue, footprintCellLimits } from "./footprint-cell.ts";
import type { FrameDepth } from "./ground-depth.ts";

/**
 * The one check of a frame's Walk on and Walk under cells (ADR-131 decision 5), called wherever a depth is
 * written or read: a route, a pull, the compile and a panel. A depth that breaks a rule is refused with the first
 * rule broken, by name; nothing is clamped or repaired.
 */

/**
 * The most rectangles one frame's depth holds, both kinds together.
 */
export const MAX_GROUND_RECTANGLES = 64;

/**
 * The rule a depth is refused by. `shape`: the value is not `{ walkOn?, walkUnder?, cell? }` of rectangles of four whole
 * numbers and a cell of 8 or 4 pixels. `rectangles`: more than `MAX_GROUND_RECTANGLES`. `size`: a rectangle with a negative corner or without
 * a positive size. `span`: a rectangle reaching the span of its cell (`footprintCellLimits`) or more. `overlap`: two rectangles sharing a
 * cell, within a kind or across kinds. `outside`: a rectangle past the frame's cell grid.
 */
export type GroundDepthRule = "shape" | "rectangles" | "size" | "span" | "overlap" | "outside";

/**
 * The frame a depth is checked against: its size in pixels and the pixels of one collision cell.
 */
export interface GroundDepthSize {
  readonly w: number;
  readonly h: number;
  readonly cellPixels: number;
}

/**
 * What the check answers: the depth, copied with its known fields alone, or the first rule broken and a message
 * naming the path and the value.
 */
export type FrameDepthResult = { readonly ok: true; readonly depth: FrameDepth } | { readonly ok: false; readonly rule: GroundDepthRule; readonly message: string };

const KINDS = ["walkOn", "walkUnder"] as const;
const FIELDS: readonly string[] = [...KINDS, "cell"];

/**
 * Reads a list of rectangles of four whole numbers.
 *
 * @param value The value.
 * @returns The rectangles, copied, or null when the value is not such a list.
 */
function boxesOf(value: unknown): CellBox[] | null {
  if (!Array.isArray(value) || !value.every((box) => Array.isArray(box) && box.length === 4 && box.every((n) => typeof n === "number" && Number.isInteger(n)))) {
    return null;
  }
  return (value as number[][]).map((box) => [box[0] as number, box[1] as number, box[2] as number, box[3] as number]);
}

const OLD_KEYS = ["floor", "lines"] as const;

/**
 * Reads a depth saved in ADR-098's shape, `{ floor, lines }`, as a depth of today (ADR-131 decision 5): the floor
 * rectangles, which share the `[column, rowUp, columns, rows]` form, become `walkOn` and the lines are dropped,
 * since the parts come from the footprint. Nothing else is touched: a depth of the new shape, one that mixes old and
 * new keys, one with another key, a floor that is not a list of rectangles and a value that is no object come back
 * as the same value, for the validator to refuse or take as it did.
 *
 * @param value The stored depth.
 * @returns The depth with `walkOn` in place of `floor` and without `lines`, or the value itself.
 */
export function normaliseFrameDepth(value: unknown): unknown {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return value;
  }
  const keys = Object.keys(value);
  if (keys.length === 0 || !keys.every((key) => (OLD_KEYS as readonly string[]).includes(key))) {
    return value;
  }
  const { floor } = value as Record<string, unknown>;
  if (floor === undefined) {
    return {};
  }
  const boxes = boxesOf(floor);
  return boxes === null ? value : { walkOn: boxes };
}

/**
 * Whether two rectangles share a cell.
 *
 * @param a One rectangle.
 * @param b The other.
 * @returns True when they overlap.
 */
function overlaps(a: CellBox, b: CellBox): boolean {
  return a[0] < b[0] + b[2] && b[0] < a[0] + a[2] && a[1] < b[1] + b[3] && b[1] < a[1] + a[3];
}

/**
 * A refusal.
 *
 * @param rule The rule broken.
 * @param message What is wrong, naming the path.
 * @returns The result.
 */
function refuse(rule: GroundDepthRule, message: string): FrameDepthResult {
  return { ok: false, rule, message };
}

/**
 * Checks a value as a frame's depth. Without a frame the rules that need none apply: the shape, the count, each
 * rectangle's size and span and no overlap. With a frame every rectangle must also lie inside its cell grid, a
 * last column or top row the frame only partly covers included.
 *
 * A depth in the old shape is read through `normaliseFrameDepth` first, so a stored `floor` is never refused and
 * the depth returned holds `walkOn`.
 *
 * @param candidate The candidate depth.
 * @param frame The frame it belongs to, or undefined where its size is not known.
 * @returns The depth, or the first rule broken with its message.
 */
export function validateFrameDepth(candidate: unknown, frame?: GroundDepthSize): FrameDepthResult {
  const value = normaliseFrameDepth(candidate);
  if (typeof value !== "object" || value === null || Array.isArray(value) || !Object.keys(value).every((key) => FIELDS.includes(key))) {
    return refuse("shape", "depth must be an object of walkOn, walkUnder and cell alone");
  }
  const record = value as Record<string, unknown>;
  const cellIssue = record["cell"] === undefined ? undefined : footprintCellIssue(record["cell"]);
  if (cellIssue !== undefined) {
    return refuse("shape", `depth: ${cellIssue}`);
  }
  const depth: { walkOn?: CellBox[]; walkUnder?: CellBox[]; cell?: number } = {};
  const cell = record["cell"] === undefined ? undefined : (record["cell"] as number);
  const { span } = footprintCellLimits(cell ?? FOOTPRINT_DEFAULT_CELL);
  const boxes: { readonly box: CellBox; readonly at: string }[] = [];
  for (const kind of KINDS) {
    if (record[kind] === undefined) {
      continue;
    }
    const read = boxesOf(record[kind]);
    if (read === null) {
      return refuse("shape", `${kind} must be [[column, rowUp, columns, rows], ...] in whole numbers`);
    }
    depth[kind] = read;
    boxes.push(...read.map((box, i) => ({ box, at: `${kind}[${i}]` })));
  }
  if (boxes.length > MAX_GROUND_RECTANGLES) {
    return refuse("rectangles", `depth holds ${boxes.length} rectangles and a frame takes at most ${MAX_GROUND_RECTANGLES}`);
  }
  for (const { box, at } of boxes) {
    if (box[0] < 0 || box[1] < 0 || box[2] < 1 || box[3] < 1) {
      return refuse("size", `${at} [${box.join(", ")}] must start at 0 or more and span at least one column and one row`);
    }
    if (box[0] + box[2] > span || box[1] + box[3] > span) {
      return refuse("span", `${at} [${box.join(", ")}] takes cell coordinates under ${span}`);
    }
  }
  for (const [i, a] of boxes.entries()) {
    const other = boxes.slice(0, i).find((b) => overlaps(a.box, b.box));
    if (other !== undefined) {
      return refuse("overlap", `${other.at} and ${a.at} overlap`);
    }
  }
  if (frame !== undefined) {
    const columns = Math.ceil(frame.w / (cell ?? frame.cellPixels));
    const rows = Math.ceil(frame.h / (cell ?? frame.cellPixels));
    for (const { box, at } of boxes) {
      if (box[0] + box[2] > columns || box[1] + box[3] > rows) {
        return refuse("outside", `${at} [${box.join(", ")}] lies outside the frame's ${columns} by ${rows} cells`);
      }
    }
  }
  return { ok: true, depth: cell === undefined || cell === FOOTPRINT_DEFAULT_CELL ? depth : { ...depth, cell } };
}
